import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ImageBackground,
  FlatList,
  ScrollView,
  Pressable,
  Linking,
  ActivityIndicator,
  useWindowDimensions,
} from "react-native";
import { Avatar, GiftedChat, Time } from "react-native-gifted-chat";
import { UserAvatar } from "../../components/user-avatar";
import useUniqueDispatch, {
  isData,
  isLoading,
  useSelector,
} from "../../utils/redux-utils";
import { RootState } from "../../store/root-reducer";
import { fonts } from "../../common/styles/fonts";
import { colors } from "../../common/styles/colors";
import { ReactComponent as SendButtonIcon } from "../../common/svg/send_button.svg";
import axios from "axios";
import {
  chatbotGetChatAction,
  chatbotPostMessageAction,
} from "../../store/chatbot/action";
import { PlaceCardMessage } from "../../components/PlaceCardMessage";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "../../common/icons/ArrowLeft";
import {
  chatbotListDeleteChatAction,
  chatbotListGetChatListAction,
  chatbotListPostChatAction,
  chatbotListUpdateChatAction,
} from "../../store/chatbotList/action";
import { GadderLogoBlack } from "../../common/icons/GadderLogoBlack";
import { MascotBlack } from "../../common/icons/mascot_black";
import { ReactComponent as ChatItemMenu } from "../../common/svg/chat_menu_background.svg";
import { ReactComponent as InstagramIcon } from "../../common/svg/instagram_icon.svg";
import { ReactComponent as ArrowIcon } from "../../common/svg/arrow_short_black.svg";
import { ReactComponent as BurgerIcon } from "../../common/svg/burger.svg";
import { ReactComponent as DownloadIcon } from "../../common/svg/download.svg";
import { CommonButton } from "../../components/buttons";
import { DrawerWrapper } from "../../components/drawerWrapper";
import { Chat } from "../../components/chat";

export const useIsMobile = () => {
  const dimensions = useWindowDimensions();
  return dimensions.width <= 960;
};

export const ChatPage = () => {
  const { state } = useLocation();

  const isMobile = useIsMobile();

  const [selectedChatId, setSelectedChatId] = useState<number | null>(
    state?.chatId || null
  );

  // console.log("selectedChatId", selectedChatId);

  const chatRequest = useSelector((store: RootState) => store.chatbot);

  // console.log("chatRequest", chatRequest?.data?.id);

  const [getChatToken, getChat] = useUniqueDispatch(
    chatbotGetChatAction.request
  );

  const isChatLoading = isLoading(chatRequest, getChatToken);

  const [submitMessageToken, submitMessage] = useUniqueDispatch(
    chatbotPostMessageAction.request
  );

  const isBotRespondLoading = isLoading(chatRequest, submitMessageToken);

  const onSubmitMessage = (chatId: number, message: string) => {
    // console.log("chatId", chatId);
    submitMessage([{ chat_id: chatId, message_text: message }]);
  };

  useEffect(() => {
    if (selectedChatId) {
      getChat([selectedChatId]);
    }
  }, [selectedChatId]);

  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(!selectedChatId);

  return (
    <View style={{ flex: 1, flexDirection: isMobile ? "column" : "row" }}>
      {!isMobile && (
        <ChatListPanel
          selectedChatId={selectedChatId}
          setSelectedChatId={setSelectedChatId}
        />
      )}
      {isChatLoading && chatRequest.data?.id !== selectedChatId ? (
        <ActivityIndicator color={colors.black} style={{ flex: 1 }} />
      ) : (
        <>
          {isMobile && (
            <View
              style={{
                height: 54,
                borderBottomWidth: 1,
                borderBottomColor: colors.gray81,
                justifyContent: "space-between",
                alignItems: "center",
                flexDirection: "row",
                paddingHorizontal: 32,
              }}
            >
              <TouchableOpacity onPress={() => setIsMobileDrawerOpen(true)}>
                <BurgerIcon width={28} height={28} />
              </TouchableOpacity>
              <CommonButton
                onPress={() =>
                  Linking.openURL("https://8fmvt.app.link/tvGWGdoXwwb")
                }
                type={"blackFilled"}
                textStyle={fonts.mediumText_medium}
                containerStyle={[
                  {
                    paddingHorizontal: 12,
                    paddingTop: 4,
                    paddingBottom: 6,
                    alignItems: "center",
                  },
                ]}
                title={"App"}
                RightIcon={
                  <DownloadIcon
                    style={{ marginLeft: 6 }}
                    width={16}
                    height={16}
                  />
                }
              />
            </View>
          )}
          <Chat
            isLoading={isBotRespondLoading}
            onSubmitMessage={onSubmitMessage}
            chat={chatRequest.data}
          />
        </>
      )}
      {isMobile && (
        <DrawerWrapper
          onClose={() => setIsMobileDrawerOpen(false)}
          isOpen={isMobileDrawerOpen}
        >
          <ChatListPanel
            selectedChatId={selectedChatId}
            setSelectedChatId={(id) => {
              setSelectedChatId(id);
              setIsMobileDrawerOpen(false);
            }}
          />
        </DrawerWrapper>
      )}
    </View>
  );
};

const ChatListPanel = ({
  selectedChatId,
  setSelectedChatId,
}: {
  setSelectedChatId: (id: number) => void;
  selectedChatId: number | null;
}) => {
  const navigate = useNavigate();

  const backRoute = () => {
    navigate("../subscription/profile", { replace: true });
  };

  const chatbotListRequest = useSelector(
    (store: RootState) => store.chatbotList
  );

  const chatbotRequest = useSelector((store: RootState) => store.chatbot);

  const [getChatListToken, getChatList] = useUniqueDispatch(
    chatbotListGetChatListAction.request
  );

  const isChatListLoading = isLoading(chatbotListRequest, getChatListToken);

  const [createChatToken, createChat] = useUniqueDispatch(
    chatbotListPostChatAction.request
  );

  const [deleteChatToken, deleteChat] = useUniqueDispatch(
    chatbotListDeleteChatAction.request
  );

  const [renameChatToken, renameChat] = useUniqueDispatch(
    chatbotListUpdateChatAction.request
  );

  const isChatCreated = isData(chatbotListRequest, createChatToken);

  useEffect(() => {
    if (
      chatbotListRequest.data &&
      chatbotListRequest.lastOpId === createChatToken
    ) {
      setSelectedChatId(
        chatbotListRequest.data[chatbotListRequest.data.length - 1].id
      );
    }
  }, [chatbotListRequest.lastOpId]);

  useEffect(() => {
    getChatList([]);
  }, []);

  useEffect(() => {
    if (chatbotListRequest.data) {
      if (chatbotListRequest.data.length > 0 && selectedChatId === null) {
        setSelectedChatId(
          chatbotListRequest.data?.sort(
            (a, b) =>
              new Date(b.lastMessagesDate) - new Date(a.lastMessagesDate)
          )[0].id
        );
      }
    }
  }, [chatbotListRequest.data]);

  const isChatsLoaded = isData(chatbotListRequest, getChatListToken);

  useEffect(() => {
    if (isChatsLoaded && isChatsLoaded.length === 0) {
      createChat([{}]);
    }
  }, [isChatsLoaded]);

  const createChatHandler = () => {
    createChat([{}]);
  };

  const deleteHandler = (id: number) => {
    deleteChat([id]);
  };

  const renameChatHandler = (id: number, name: string) => {
    renameChat([id, { name }]);
  };

  const user = useSelector((store: RootState) => store.user);

  const [detailMenuOpen, setDetailMenuOpen] = useState(false);

  const isMobile = useIsMobile();

  return (
    <View
      style={{
        height: "100%",
        width: isMobile ? "100%" : 472,
        backgroundColor: "white",
        borderRightWidth: 1,
        borderRightColor: colors.gray81,
        paddingTop: 24,
      }}
    >
      <TouchableOpacity
        onPress={backRoute}
        style={{
          flexDirection: "row",
          width: "100%",
          paddingHorizontal: 24,
          paddingBottom: 12,
          alignItems: "center",
        }}
      >
        <UserAvatar size={64} user={user.data} />

        <Text
          style={[
            fonts.largeText_bold,
            { color: colors.black, marginLeft: 24 },
          ]}
        >
          {user.data.first_name + " " + user.data.second_name}
        </Text>
      </TouchableOpacity>

      <TouchableOpacity>
        <View
          style={{
            width: "100%",
            borderRadius: 6,
            marginHorizontal: 24,
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <InstagramIcon width={24} />
          <Text
            style={[
              fonts.mediumText_medium,
              { color: colors.black, marginLeft: 12 },
            ]}
          >
            Follow us
          </Text>
        </View>
      </TouchableOpacity>

      <View
        style={{
          width: "100%",
          height: 1,
          backgroundColor: colors.gray81,
        }}
      />

      <ScrollView
        contentContainerStyle={{ overflow: "visible", paddingTop: 12 }}
        style={{ zIndex: 10 }}
      >
        {isChatListLoading && !chatbotListRequest.data ? (
          <ActivityIndicator color={colors.black} style={{ marginTop: 24 }} />
        ) : (
          <View style={{ paddingHorizontal: 16, flex: 1, overflow: "visible" }}>
            <TouchableOpacity onPress={createChatHandler}>
              <View
                style={{
                  width: "100%",
                  backgroundColor: "#F7F6F6",
                  paddingHorizontal: 8,
                  paddingVertical: 6,
                  borderRadius: 6,
                  marginBottom: 12,
                }}
              >
                <Text
                  style={[fonts.mediumText_medium, { color: colors.black }]}
                >
                  New Chat
                </Text>
              </View>
            </TouchableOpacity>

            {chatbotListRequest.data
              ?.sort(
                (a, b) =>
                  new Date(b.lastMessagesDate) - new Date(a.lastMessagesDate)
              )
              .map((item) => (
                <ChatListItem
                  isSelected={item.id === selectedChatId}
                  onSelect={setSelectedChatId}
                  onSave={renameChatHandler}
                  onDelete={deleteHandler}
                  chat={item}
                  isOne={chatbotListRequest.data.length === 1}
                />
              ))}
          </View>
        )}
      </ScrollView>

      <View
        style={{
          width: "100%",
          height: 1,
          backgroundColor: colors.gray81,
        }}
      />

      <View>
        <TouchableOpacity
          onPress={() => setDetailMenuOpen((prev) => !prev)}
          style={{
            flexDirection: "row",
            alignItems: "center",
            paddingHorizontal: 24,
            paddingVertical: 12,
          }}
        >
          <Text
            style={[
              fonts.mediumText_bold,
              { flex: 1, color: colors.black, fontWeight: "500" },
            ]}
          >
            Information and support
          </Text>

          <View
            style={{
              transform: [{ rotate: detailMenuOpen ? "270deg" : "90deg" }],
              width: 20,
            }}
          >
            <ArrowIcon />
          </View>
        </TouchableOpacity>

        {detailMenuOpen && (
          <View style={{ paddingHorizontal: 16 }}>
            <View style={[styles.roundButton, { alignSelf: "flex-start" }]}>
              Contact: hello@gadderapp.com
            </View>

            <View
              style={{
                alignItems: "center",
                gap: 12,
                flexDirection: "row",
                marginTop: 12,
              }}
            >
              <TouchableOpacity
                onPress={() =>
                  Linking.openURL(
                    "https://www.termsfeed.com/live/fb6d3a5b-41e8-4e4a-9cd6-fee3c8e58f54"
                  )
                }
                style={[styles.roundButton]}
              >
                Privacy Policy
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() =>
                  Linking.openURL("https://www.gadderapp.com/faqs")
                }
                style={[styles.roundButton]}
              >
                FAQs
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() =>
                  Linking.openURL("https://www.gadderapp.com/d-and-i")
                }
                style={[styles.roundButton]}
              >
                D&I
              </TouchableOpacity>
            </View>
          </View>
        )}
      </View>

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 24,
        }}
      >
        <MascotBlack width={35} style={{ marginRight: 12 }} />
        <GadderLogoBlack width={100} />
      </View>
    </View>
  );
};

const ChatListItem = ({
  isSelected,
  chat,
  onSave,
  onDelete,
  onSelect,
  isOne = false,
}: {
  isSelected?: boolean;
  chat: { name: string; id: number };
  onDelete: (id: number) => void;
  onSave: (id: number, name: string) => void;
  onSelect: (id: number) => void;
  isOne?: boolean;
}) => {
  const [chatName, setChatName] = useState(chat.name);
  const [renameMode, setRenameMod] = useState(false);

  const [menuOpen, setMenuOpen] = useState(false);

  const menuRef = useRef(null);

  const handleOutsideClick = useCallback(
    (event) => {
      if (
        !renameMode &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    },
    [renameMode]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [handleOutsideClick]);

  const [isHover, setIsHover] = useState(false);

  return (
    <View
      style={{
        width: "100%",
        paddingHorizontal: 8,
        paddingVertical: 6,
        borderRadius: 6,
        flexDirection: "row",
        backgroundColor: isSelected ? "#F1F1F1" : isHover ? "#F7F6F6" : "white",
        zIndex: 1000,
        position: "relative",
      }}
    >
      <View style={{ flex: 1 }}>
        {renameMode ? (
          <TextInput
            style={[fonts.mediumText_regular, { color: colors.black }]}
            placeholder={"Input chat name"}
            editable={renameMode}
            onChangeText={setChatName}
            value={chatName}
          />
        ) : (
          <Pressable
            onHoverIn={() => setIsHover(true)}
            onHoverOut={() => setIsHover(false)}
            onPress={() => onSelect(chat.id)}
          >
            <Text
              numberOfLines={1}
              style={[
                isSelected ? fonts.mediumText_medium : fonts.mediumText_regular,
                { color: colors.black },
              ]}
            >
              {chat.name || "New Chat"}
            </Text>
          </Pressable>
        )}
      </View>

      <TouchableOpacity
        onPress={() => setMenuOpen(true)}
        style={styles.dotContainer}
      >
        <View style={styles.dot} />
        <View style={styles.dot} />
        <View style={styles.dot} />
      </TouchableOpacity>

      {menuOpen && (
        <View
          ref={menuRef}
          style={{
            position: "absolute",
            right: -17,
            top: -50,
            zIndex: 20,
            transform: [{ scale: 0.9 }],
          }}
        >
          <ChatItemMenu style={{ position: "absolute" }} />
          <View style={{ alignItems: "center", width: 92, height: 64 }}>
            <Text
              onPress={() => {
                if (renameMode) {
                  onSave(chat.id, chatName);
                  setMenuOpen(false);
                }
                setRenameMod(!renameMode);
              }}
              style={[{ paddingTop: isOne ? 18 : 6, paddingBottom: 6 }]}
            >
              {renameMode ? "Save" : "Rename"}
            </Text>
            {!isOne && (
              <>
                <View
                  style={{ width: 49, height: 1, backgroundColor: "#979797" }}
                />
                <Text
                  onPress={() => {
                    if (!renameMode) {
                      onDelete(chat.id);
                    } else {
                      setRenameMod(false);
                    }

                    setMenuOpen(false);
                  }}
                  style={{ paddingTop: 3 }}
                >
                  {renameMode ? "Cancel" : "Delete"}
                </Text>
              </>
            )}
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  roundButton: {
    ...fonts.smallMediumText_medium,
    color: colors.black,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 2,
    borderColor: colors.black,
  },
  dotContainer: {
    marginLeft: 16,
    position: "relative",
    flexDirection: "row",
    gap: 2,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  dot: {
    width: 4,
    height: 4,
    borderRadius: 4,
    backgroundColor: colors.noteGray,
  },
});
